import React, { FC } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ListUploadStatusBar from './ListUploadStatusBar';
import { useGetListUpload } from '../../hooks/api/ListUpload/useGetListUpload';
import { useSawInProgress } from '../../hooks/useSawInProgress';
import { isInProgressStatus } from '../../utility/listUploadProgress';

const queryClient = new QueryClient();

type ListUploadProgressBannerProps = {
  listUploadId: string;
  onRefresh?: () => void;
}

// Shown on the list sort page while the list it was built from is still
// being processed, so the missing data isn't a mystery.
const ListUploadProgressBannerChild: FC<ListUploadProgressBannerProps> = ({ listUploadId, onRefresh }) => {
  const { listUpload } = useGetListUpload(listUploadId, undefined);
  const sawInProgress = useSawInProgress(listUpload?.status);

  const status: string | undefined = listUpload?.status;
  const showBanner = !!status && (isInProgressStatus(status) || (sawInProgress && (status === 'Complete' || status === 'Error')));
  if (!showBanner) return null;

  return (
    <div className='rounded-lg border border-indigo-100 bg-indigo-50 p-4'>
      <p className='mb-3 text-sm text-gray-700'>
        Your list is still being processed, so some property data may be missing until it finishes.
      </p>
      <ListUploadStatusBar status={status} progressPercent={listUpload.progress_percent} onRefresh={onRefresh} />
    </div>
  );
};

const ListUploadProgressBanner: FC<ListUploadProgressBannerProps> = (props) => (
  <QueryClientProvider client={queryClient}>
    <ListUploadProgressBannerChild {...props} />
  </QueryClientProvider>
);

export default ListUploadProgressBanner;
